import React, { SetStateAction, Dispatch } from "react";
import {useTranslation} from "react-i18next";
import Button from "@awsui/components-react/button";
import { useMutation } from "react-query";
import addMembership from "api/addMembership";
import { FlashbarProps } from "@awsui/components-react/flashbar/interfaces";
import { useAuth } from "hooks/useAuth";
import League from "model/League";
import WrongPasswordError from "api/errors/WrongPasswordError";
import AlreadyInLeagueError from "api/errors/AlreadyInLeagueError";


interface JoinLeagueButtonProps {
    league: League;
    setSubmitMessage: Dispatch<SetStateAction<FlashbarProps.MessageDefinition | undefined>>;
    buttonEnabled: boolean;
    setButtonEnabled?: Dispatch<SetStateAction<boolean>>;
}

const JoinLeagueButton: React.FC<JoinLeagueButtonProps> = (props) => {

    const {t} = useTranslation(["leagues", "common"]);
    const leagueToJoin = props.league;

    const [{ user, isSignedIn, isAdmin }, dispatch] = useAuth();

    const {
        isLoading: addMembershipIsLoading,
        mutate: callAddMembership
    } = useMutation((league: League) => addMembership(league, user?.token), {
        onSuccess: () => {
            props.setSubmitMessage({
                header: t("leagues:joinedSuccessHeader", {name: leagueToJoin.name}),
                type: "success",
                content: "",
            });
            user?.leagues && dispatch({
                isSignedIn: isSignedIn,
                isAdmin: isAdmin,
                user: {
                    ...user,
                    leagues: [
                        ...user?.leagues,
                        {
                            ...leagueToJoin,
                            members: leagueToJoin.members + 1
                        }
                    ]
                }
            });
        },
        onError: (error: Error) => {
            if (error instanceof WrongPasswordError) {
                props.setSubmitMessage({
                    header: t("leagues:joinErrorHeader", {name: leagueToJoin.name}),
                    type: "error",
                    content: t("leagues:wrongPassword"),
                });
            } else if (error instanceof AlreadyInLeagueError) {
                props.setSubmitMessage({
                    header: t("leagues:joinErrorHeader", {name: leagueToJoin.name}),
                    type: "error",
                    content: t("leagues:alreadyInLeague", {name: leagueToJoin.name}),
                });
            } else {
                props.setSubmitMessage({
                    header: t("leagues:joinErrorHeader", {name: leagueToJoin.name}),
                    type: "error",
                    content: t("common:errors.tryAgain"),
                });
            }
        },
        onSettled: () => {
            props.setButtonEnabled && props.setButtonEnabled(false);
        }
    });

    const joinLeague = () => {
        props.setSubmitMessage(undefined);
        callAddMembership({
            ...leagueToJoin,
            password: btoa(leagueToJoin.password || "")
        });
    };

    return (
        <Button
            disabled={!props.buttonEnabled}
            iconAlign="left"
            loading={addMembershipIsLoading}
            variant="primary"
            onClick={joinLeague}
        >
            {t("leagues:joinButton")}
        </Button>
    );
};

export default JoinLeagueButton;
